import { useQuery } from '@tanstack/react-query';
import { Role } from '@pillar-portal/database/dist/frontend';
import type {
    ActiveOrganizationSignatureSummary,
    ActiveRoleAssignmentOperationalResponse,
    ActiveRoleAssignmentResponse,
} from '@pillar-portal/api-types';
import { getActiveRoleAssignmentOperationalResponse } from '@/lib/active-role-assignment';
import { legalSurfacingApi } from '@/lib/pillarApi';
import { sessionSensitiveAuthApi } from '@/lib/sessionSensitiveAuthApi';
import { runInBackground } from '@/lib/utils';
import { useAuthStore } from '@/store/auth';

export interface ActiveOrganizationSignatureSummaryState {
    readonly activeRoleAssignment: ActiveRoleAssignmentOperationalResponse | null;
    /** Undefined while loading, or when the active role never signs or sees signatures. */
    readonly summary: ActiveOrganizationSignatureSummary | undefined;
    readonly isLoading: boolean;
    readonly isError: boolean;
    readonly refetch: () => void;
}

/**
 * Organization agreements awaiting signature for the active PRINCIPAL, ADMIN
 * or REP RoleAssignment. The query sits under the Organization agreements key
 * prefix, so signing an agreement refreshes it.
 */
export function useActiveOrganizationSignatureSummary(): ActiveOrganizationSignatureSummaryState {
    const user = useAuthStore((state) => state.user);
    // A session awaiting a password change may read only its profile; any
    // other request is refused with 401 and would sign the user out.
    const requiresPasswordChange: boolean = user?.requires_password_change === true;
    const activeRoleAssignmentQuery = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => await sessionSensitiveAuthApi.getActiveRoleAssignment(),
        enabled: !!user && !requiresPasswordChange,
        staleTime: 30000,
    });
    const activeRoleAssignment = getActiveRoleAssignmentOperationalResponse(
        activeRoleAssignmentQuery.data
    );
    const organizationUuid =
        activeRoleAssignment !== null &&
        (activeRoleAssignment.role === Role.PRINCIPAL ||
            activeRoleAssignment.role === Role.ADMIN ||
            activeRoleAssignment.role === Role.REP)
            ? activeRoleAssignment.organization_uuid
            : null;
    const summaryQuery = useQuery<ActiveOrganizationSignatureSummary>({
        queryKey: ['organizationAgreements', organizationUuid, 'signatureSummary'],
        queryFn: async () => await legalSurfacingApi.getActiveOrganizationSignatureSummary(),
        enabled: organizationUuid !== null,
        staleTime: 60000,
    });
    return {
        activeRoleAssignment,
        summary: organizationUuid === null ? undefined : summaryQuery.data,
        isLoading:
            activeRoleAssignmentQuery.isLoading ||
            (organizationUuid !== null && summaryQuery.isLoading),
        isError: activeRoleAssignmentQuery.isError || summaryQuery.isError,
        refetch: () => {
            runInBackground(summaryQuery.refetch(), () => undefined);
        },
    };
}
